import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Shield, BarChart3 } from 'lucide-react';

const STORAGE_KEY = 'dts_cookie_consent_v2';

export const getCookieConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const setCookieConsent = (value) => {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ ...value, timestamp: new Date().toISOString(), version: 'v2' }),
  );
  // Fire a global event so other components can react
  window.dispatchEvent(new CustomEvent('cookie-consent-change', { detail: value }));
};

const DEFAULT_PREFS = { necessary: true, analytics: false, marketing: false };

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    if (!getCookieConsent()) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
    // allow the Footer "Modifica preferenze" link to reopen the banner
    const onReopen = () => { setShowPrefs(true); setVisible(true); setPrefs(getCookieConsent() || DEFAULT_PREFS); };
    window.addEventListener('cookie-consent-reopen', onReopen);
    return () => window.removeEventListener('cookie-consent-reopen', onReopen);
  }, []);

  const acceptAll = () => {
    setCookieConsent({ necessary: true, analytics: true, marketing: true });
    setVisible(false);
    setShowPrefs(false);
  };
  const rejectAll = () => {
    setCookieConsent({ necessary: true, analytics: false, marketing: false });
    setVisible(false);
    setShowPrefs(false);
  };
  const savePrefs = () => {
    setCookieConsent({ ...prefs, necessary: true });
    setVisible(false);
    setShowPrefs(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Preferenze cookie"
      data-testid="cookie-banner"
      className="fixed bottom-4 left-24 right-4 md:left-28 md:right-auto md:max-w-md z-[55] glass rounded-2xl p-5 shadow-2xl border border-lime/20"
    >
      <div className="flex items-start gap-3 mb-4">
        <span className="w-10 h-10 rounded-xl bg-lime/15 flex items-center justify-center shrink-0">
          <Cookie size={20} className="text-lime" />
        </span>
        <div className="flex-1">
          <p className="text-white font-semibold mb-1">Utilizziamo i cookie</p>
          <p className="text-white/60 text-sm leading-relaxed">
            Utilizziamo cookie tecnici necessari al funzionamento del sito e, previo tuo consenso, cookie di analisi e marketing. Puoi scegliere liberamente se accettare, rifiutare o personalizzare. Scopri di piu nella{' '}
            <Link to="/cookie-policy" className="text-lime underline">Cookie Policy</Link> e nella{' '}
            <Link to="/privacy-policy" className="text-lime underline">Privacy Policy</Link>.
          </p>
        </div>
      </div>

      {showPrefs && (
        <div className="space-y-2 mb-4 border-t border-white/10 pt-4">
          <div className="flex items-start gap-3">
            <input type="checkbox" checked disabled className="mt-1 accent-lime" />
            <div className="flex-1">
              <p className="text-white text-sm font-semibold flex items-center gap-2">
                <Shield size={14} className="text-lime" /> Necessari (sempre attivi)
              </p>
              <p className="text-white/50 text-xs mt-0.5">Autenticazione, sessione, memorizzazione preferenze.</p>
            </div>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={prefs.analytics}
              onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
              className="mt-1 accent-lime"
              data-testid="cookie-pref-analytics"
            />
            <div className="flex-1">
              <p className="text-white text-sm font-semibold flex items-center gap-2">
                <BarChart3 size={14} className="text-lime" /> Analitici
              </p>
              <p className="text-white/50 text-xs mt-0.5">Statistiche aggregate anonime (non attivi al momento).</p>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={prefs.marketing}
              onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
              className="mt-1 accent-lime"
              data-testid="cookie-pref-marketing"
            />
            <div className="flex-1">
              <p className="text-white text-sm font-semibold">Marketing</p>
              <p className="text-white/50 text-xs mt-0.5">Comunicazioni personalizzate e remarketing (non attivi al momento).</p>
            </div>
          </label>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          onClick={acceptAll}
          data-testid="cookie-accept-all"
          className="px-5 py-2.5 rounded-full bg-lime text-background text-sm font-semibold btn-glow transition-all hover:scale-105"
        >
          Accetta tutti
        </button>
        <button
          onClick={rejectAll}
          data-testid="cookie-reject-all"
          className="px-5 py-2.5 rounded-full border border-white/25 text-white text-sm hover:border-lime hover:text-lime transition-colors"
        >
          Rifiuta tutti
        </button>
        {showPrefs ? (
          <button
            onClick={savePrefs}
            data-testid="cookie-save-prefs"
            className="px-5 py-2.5 rounded-full border border-lime text-lime text-sm hover:bg-lime hover:text-background transition-colors"
          >
            Salva preferenze
          </button>
        ) : (
          <button
            onClick={() => setShowPrefs(true)}
            data-testid="cookie-personalize"
            className="px-5 py-2.5 rounded-full text-white/60 text-sm hover:text-white transition-colors"
          >
            Personalizza
          </button>
        )}
      </div>
    </div>
  );
}
